import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MessageTemplateConfig from "../MessageTemplateConfig";
import ButtonTemplateConfig from "../ButtonTemplateConfig";
import ContextualHint from "../components/discovery/ContextualHint";


function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AutoCommentConfigPage({ token, pageId: propPageId, selectedPageData }) {
  const navigate = useNavigate();
  const query = useQuery();

  const postId = query.get("postId") || undefined;
  const pageId = propPageId || query.get("pageId") || undefined;
  const platform = selectedPageData?.hasInstagram ? "instagram" : "facebook";

  const [templateType, setTemplateType] = useState("ButtonTemplate");
  const [postData, setPostData] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);

  useEffect(() => {
    const fetchPostData = async () => {
      if (!postId || !pageId) {
        console.log('[AutoCommentConfigPage] Missing postId or pageId:', { postId, pageId });
        return;
      }
      
      console.log('[AutoCommentConfigPage] Fetching post data:', { postId, pageId, platform });
      setLoadingPost(true);
      try {
        const endpoint = platform === "facebook" ? "facebook" : "instagram";
        console.log(`[AutoCommentConfigPage] Calling /${endpoint}/posts/${pageId}`);
        
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/${endpoint}/posts/${pageId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log(`[AutoCommentConfigPage] Got ${response.data?.length || 0} posts from API`);
        let post = (response.data || []).find((p) => p.id === postId);

        if (!post) {
          console.log('[AutoCommentConfigPage] Post not found in API response, trying message-count endpoint');
          const mc = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/media/message-count`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { pageId },
            }
          );
          console.log(`[AutoCommentConfigPage] Got ${mc.data?.length || 0} posts from message-count`);
          post = (mc.data || []).find((p) => p.id === postId);
        }

        if (post) {
          console.log('[AutoCommentConfigPage] Found post:', post.id);
          setPostData(post);
        } else {
          console.warn('[AutoCommentConfigPage] Post not found in either endpoint');
        }
      } catch (e) {
        console.error('[AutoCommentConfigPage] Error fetching post data:', e.response?.data || e.message);
      } finally {
        setLoadingPost(false);
      }
    };
    fetchPostData();
  }, [postId, pageId, token, platform]);

  return (
    <div className="py-2">
      <div className="mb-0">
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
        >
          Back
        </Button>
      </div>
      <Typography
        variant="h6"
        className="text-2xl font-bold text-gray-900 dark:text-white mb-6"
      >
        Configure Auto DM for Comments Bot
      </Typography>



      <ContextualHint
        id="button-template-hint"
        variant="success"
        message='⚡ Quick Win: Use "Button Template" and click "Choose Product" to auto-fill everything in one click!'
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left: Post information (25%) */}
        <div className="md:col-span-3">
          <div className="card-modern p-6">
            <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Post Information
            </Typography>
            <div className="border-t border-gray-200 dark:border-gray-700 mb-4"></div>

            {loadingPost ? (
              <div className="flex justify-center p-4">
                <CircularProgress size={20} />
              </div>
            ) : postData ? (
              <>
                <div className="w-full max-w-xs rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-4">
                  <img
                    src={
                      postData.media_type === "VIDEO"
                        ? postData.thumbnail_url
                        : postData.media_url || "/placeholder-image.png"
                    }
                    alt="Post thumbnail"
                    className="w-full h-auto block"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-image.png";
                    }}
                  />
                </div>
                {postData.caption && (
                  <Typography
                    variant="caption"
                    className="text-gray-600 dark:text-gray-400 text-xs block mt-2"
                  >
                    {postData.caption}
                  </Typography>
                )}
                {postData.timestamp && (
                  <Typography
                    variant="caption"
                    className="text-gray-600 dark:text-gray-400 text-xs block mt-2"
                  >
                    {new Date(postData.timestamp).toLocaleString()}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" className="text-gray-600 dark:text-gray-400">
                Select a post to configure templates.
              </Typography>
            )}
          </div>
        </div>

        {/* Right: Configuration (75%) */}
        <div className="md:col-span-9">
          <div className="card-modern p-6 overflow-visible">
            <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Configuration
            </Typography>

            <div className="flex justify-center mb-6">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={templateType}
                onChange={(_, v) => v && setTemplateType(v)}
                className="bg-gray-100 dark:bg-gray-800"
              >
                <ToggleButton
                  value="ButtonTemplate"
                  className={`normal-case ${templateType === "ButtonTemplate" ? "bg-primary-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                >
                  Button Template
                </ToggleButton>
                <ToggleButton
                  value="MessageTemplate"
                  className={`normal-case ${templateType === "MessageTemplate" ? "bg-primary-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                >
                  Message Template
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <div className="overflow-visible min-h-0">
              {templateType === "MessageTemplate" ? (
                <MessageTemplateConfig
                  key={`message-${postId}`}
                  token={token}
                  postId={postId}
                  onClose={() => {}}
                />
              ) : (
                <ButtonTemplateConfig
                  key={`button-${postId}`}
                  token={token}
                  postId={postId}
                  pageId={pageId}
                  onClose={() => {}}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
